// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createDataProvider } from '@shared/ui-kit/providers/data-provider/create-data-provider';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useIdleTimer } from 'react-idle-timer';
import {
  selectAuthenticationError,
  selectIsAuthenticatedUser,
  selectToken,
  selectUserData,
} from '@shared/data-access/slices/user';
import { selectRefreshToken } from '@shared/data-access/slices/auth';
import { authApi } from '@shared/data-access/api/auth0/auth-api';
import { PKCE_VERIFIER_KEY } from '@shared/data-access/utils/constants';
import {
  getBrandDomainByHostname,
  environment,
  isDevelopment,
} from '@shared/environment';
import detectIncognito from 'detectincognitojs';
import {
  IDLE_TIMEOUT_MS,
  MODAL_TEMPLATES_OPTIONS,
  showModal,
  type ShowModalOptions,
} from '@shared/ui-kit';
import { skipToken } from '@reduxjs/toolkit/query';
import { resolvePostLoginRedirect } from '../utils/post-login-redirect';

export interface AuthProviderProps {
  readonly onLogout?: () => void;
}

export const useAuthenticationFactory = ({ onLogout }: AuthProviderProps = {}) => {
  const { t, i18n } = useTranslation();

  const language = useMemo(() => i18n.language, [i18n.language]);

  const domain = useMemo(
    () =>
      typeof window === 'undefined'
        ? 'citec'
        : getBrandDomainByHostname(window.location.hostname),
    [],
  );

  const isAuthenticated = useSelector(selectIsAuthenticatedUser);

  const loginError = useSelector(selectAuthenticationError);

  const user = useSelector(selectUserData);

  const token = useSelector(selectToken);

  const refreshToken = useSelector(selectRefreshToken);

  const [getLoginUrl] = authApi.useGetLoginUrlMutation();

  const [getLogoutUrl] = authApi.useGetLogoutUrlMutation();

  const [revokeToken] = authApi.useRevokeTokenMutation({
    fixedCacheKey: 'auth/revokeToken',
  });

  const [exchangeCode, exchangeCodeResult] = authApi.useExchangeCodeMutation({
    fixedCacheKey: 'auth/exchangeCode',
  });

  authApi.useGetUserInfoQuery(token ? undefined : skipToken);

  const authorize = useCallback(
    async (params: {
      code: string;
      redirectUri: string;
      redirectTo?: string;
    }): Promise<{ success: true } | { success: false; errorKey: string }> => {
      const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

      if (!codeVerifier) {
        return {
          success: false,
          errorKey: 'auth.callback.missing-verifier',
        };
      }

      try {
        await exchangeCode({
          grant_type: 'authorization_code',
          client_id: environment.AUTH.CLIENT_ID!,
          code: params.code,
          redirect_uri: params.redirectUri,
          code_verifier: codeVerifier,
        }).unwrap();

        sessionStorage.removeItem(PKCE_VERIFIER_KEY);

        return { success: true };
      } catch {
        return {
          success: false,
          errorKey: 'auth.callback.exchange-failed',
        };
      }
    },
    [exchangeCode],
  );

  const login = useCallback(
    async (
      mode: 'login' | 'signup' = 'login',
      postLoginRedirect?: string | null,
    ) => {
      const redirectUri = `${window.location.origin}/callback`;
      const resolvedRedirect = resolvePostLoginRedirect(postLoginRedirect);
      const state = resolvedRedirect?.token ?? Math.random().toString(36).slice(2);

      const url = await getLoginUrl({
        redirectUri,
        state,
        screenHint: mode,
        locale: language,
        domain,
      }).unwrap();

      if (url) window.location.href = url;
    },
    [domain, getLoginUrl, language],
  );

  const logout = useCallback(async () => {
    if (refreshToken) {
      revokeToken({
        client_id: environment.AUTH.CLIENT_ID!,
        token: refreshToken,
        token_type_hint: 'refresh_token',
      }).catch(() => undefined);
    }

    onLogout?.();

    const returnTo = window.location.origin;

    const url = await getLogoutUrl({ returnTo }).unwrap();

    window.location.href = url;
  }, [refreshToken, revokeToken, onLogout, getLogoutUrl]);

  const onIdle = useCallback(() => {
    showModal({
      ...MODAL_TEMPLATES_OPTIONS(t).WARN,
      title: t('auth.inactivity.title', { ns: 'auth' }),
      content: t('auth.inactivity.content', { ns: 'auth' }),
      buttons: {
        confirm: {
          show: true,
          text: t('auth.inactivity.confirm', { ns: 'auth' }),
        },
      },
    } as ShowModalOptions).then(() => logout());
  }, [t, logout]);

  const { start, pause } = useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    startManually: true,
    onIdle,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      detectIncognito().then(result => {
        /* istanbul ignore next */ if (result.isPrivate) {
          return showModal({
            ...MODAL_TEMPLATES_OPTIONS(t).WARN,
            title: t('auth.credentials-error', '', { ns: 'auth' }),
            content: t('auth.incognito-mode-error', '', { ns: 'auth' }),
          } as ShowModalOptions).then(() => logout());
        }
      });
    }, 2000);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loginError) return;

    logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginError]);

  useEffect(() => {
    if (isAuthenticated && !isDevelopment) start();
    else pause();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return {
    isAuthenticated,
    login,
    logout,
    authorize,
    user,
    loginError,
    isExchangingCode: exchangeCodeResult.isLoading,
  };
};

export const { AuthProvider, useAuthData } = createDataProvider({
  name: 'Auth',
  factory: useAuthenticationFactory,
});
